<template>
  <div>
    <!-------------------------------------------TOOLBAR------------------------------------------->
    <toolbar
      ref="toolbar"
      :items="datatableItems"
      :approve="approveBtn"
      :delete="deleteBtn"
      @search="search"
      @clear-search="clearSearch"
      @scanner="openQrScanner"
      @insert="openInsertDialog"
      @approved="approveMethod"
      @deleted="deleteMethod"
    />
    <!-----------------------------------------QR SCANNER------------------------------------------>
    <qr-scanner
      ref="qrScanner"
      @edit="openEditDialog"
      @actions="openActionsDialog"
      @select-main-part="selectMainPart"
      @changed-location="getParts"
      @close="closeQrScanner"
    />
    <!--------------------------------------INSERT EMPTY PARTS------------------------------------->
    <insert-empty-dialog ref="insertEmptyDialog" @inserted="getParts" />
    <!----------------------------------------INSERTED PARTS--------------------------------------->
    <!--TOOLBAR-->
    <v-toolbar
      density="compact"
      color="grey-darken-4"
      theme="dark"
      class="inserted-parts-toolbar"
    >
      <v-toolbar-title
        class="pointer text-body-2 text-sm-body-1 text-truncate mr-2"
        style="max-width: min(100%, 72vw)"
        @click="showInserted = !showInserted"
      >
        {{ $t("parts.insert_parts.inserted_parts") }}
      </v-toolbar-title>
      <v-spacer />
      <div class="d-flex align-center flex-shrink-0">
        <!-- Desktop: inline multi-select -->
        <v-select
          v-if="showInserted"
          v-model="selectedInsertItems"
          :items="insertItems"
          class="hidden-sm-and-down inserted-parts-status-select-toolbar"
          item-title="text"
          item-value="value"
          density="compact"
          variant="solo"
          hide-details
          multiple
          chips
          closable-chips
          label="Select part's status"
        />

        <!-- Mobile: filter dropdown -->
        <v-menu
          v-if="showInserted && $vuetify.display.smAndDown"
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="text" class="d-md-none">
              <v-badge
                v-if="selectedInsertItems.length"
                :content="selectedInsertItems.length"
                color="red-darken-2"
              >
                <v-icon>mdi-filter-variant</v-icon>
              </v-badge>
              <v-icon v-else>mdi-filter-variant</v-icon>
            </v-btn>
          </template>
          <v-list density="compact" min-width="220">
            <v-list-item
              v-for="item in insertItems"
              :key="item.value"
              @click="toggleInsertStatus(item.value)"
            >
              <template #prepend>
                <v-checkbox-btn
                  :model-value="selectedInsertItems.includes(item.value)"
                  density="compact"
                  class="mr-1"
                />
              </template>
              <v-list-item-title class="text-body-2">{{ item.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn icon variant="text" @click="showInserted = !showInserted">
          <v-icon v-if="showInserted">mdi-menu-down</v-icon>
          <v-icon v-else>mdi-menu-up</v-icon>
        </v-btn>
      </div>
    </v-toolbar>

    <!-- MOBILE STATUS FILTER (replaced by toolbar dropdown) -->
    <div v-if="false" class="inserted-parts-mobile-status d-md-none">
      <div class="inserted-parts-mobile-status__header">
        <span class="inserted-parts-mobile-status__label">
          Select part's status
        </span>
        <span class="inserted-parts-mobile-status__count">
          {{ selectedInsertItems.length }}/{{ insertItems.length }}
        </span>
      </div>
      <div class="inserted-parts-mobile-status__scroll">
        <button
          v-for="item in insertItems"
          :key="item.value"
          type="button"
          class="inserted-parts-mobile-status__chip"
          :class="{
            'inserted-parts-mobile-status__chip--active':
              selectedInsertItems.includes(item.value),
          }"
          @click="toggleInsertStatus(item.value)"
        >
          {{ item.text }}
        </button>
      </div>
    </div>

    <!-- Components inside div are rendered only when Inserted Parts toolbar is expanded.-->
    <div v-if="showInserted">
      <!--DATATABLE-->
      <datatable
        ref="datatable"
        :search="searchObject"
        :selected_items="selectedInsertItems"
        @activate-snackbar="activateSnackbar"
        @approve="approve"
        @qr="openQrDialog"
        @images="openImagesCarousel"
        @items="getDatatableItems"
        @edit="openEditDialog"
        @delete="getParts"
        @view-car="openCarDialog"
        @actions="openActionsDialog"
        @show-notes="openNotesDialog"
        @movement="openMovementDialog"
        @share="openShareDialog"
      />
      <!--QR DIALOG-->
      <qr-dialog ref="qrDialog" />
      <!--CAROUSEL-->
      <img-carousel ref="imgCarousel" />
      <!--NOTES DIALOG-->
      <notes-dialog ref="notesDialog" />
      <!-- SHARE DIALOG -->
      <share-dialog ref="shareDialog" />
    </div>

    <!------------------------------------------EDIT PART------------------------------------------>
    <!--EDIT DIALOG-->
    <edit-dialog
      ref="editDialog"
      :hide="hideEditDialog"
      @new-shipment="openShipmentDialog"
      @scan-shipment="openShipmentScanner"
      @open-img-dialog="openImgDialog"
      @open-img-editor="openImgEditor"
      @scan-main-part="scanMainPart"
      @close="getParts"
    />
    <!-- ACTIONS DIALOG -->
    <actions-dialog
      ref="actionsDialog"
      @add-client="openClientsDialog"
      @close="getParts"
    />
    <clients-dialog
      ref="clientsDialog"
      @saved="getClients"
      @closed="showActionsDialog"
    />
    <!--SHIPMENT DIALOG-->
    <shipment-dialog
      ref="shipmentDialog"
      @close="closeShipmentDialog"
      @save="saveShipment"
    />
    <!--SHIPMENT SCANNER-->
    <shipment-scanner
      ref="shipmentScanner"
      @select-shipment="selectShipment"
      @close="closeShipmentScanner"
    />
    <!--IMAGE DIALOG-->
    <img-dialog ref="imgDialog" @close="closeImgDialog" />
    <img-editor ref="imgEditor" @save="editImage" @close="closeImgDialog" />
    <movement-dialog ref="movementDialog" />
    <!-----------------------------------------EMPTY PARTS----------------------------------------->
    <!--TOOLBAR-->
    <v-toolbar color="grey-darken-4" text dark dense>
      <v-toolbar-title class="pointer" @click="showEmpty = !showEmpty">
        {{ $t("parts.insert_parts.empty_parts.empty_parts") }}
      </v-toolbar-title>
      <v-spacer />
      <div class="d-flex align-center">
        <v-select
          v-if="showEmpty"
          v-model="groupEmptyParts"
          :items="groupItemsEmptyParts"
          hide-details
          class="hidden-xs-only"
          item-title="text"
          item-value="value"
          solo
          dense
          chips
        />

        <v-btn text icon @click="showEmpty = !showEmpty">
          <v-icon v-if="showEmpty">mdi-menu-down </v-icon>
          <v-icon v-else> mdi-menu-up </v-icon>
        </v-btn>
      </div>
    </v-toolbar>

    <!--SELECTION 'XS'-->
    <v-row v-if="showEmpty" class="grey-darken-4" row wrap justify-center>
      <v-col xs="12">
        <v-select
          v-if="showEmpty"
          v-model="groupEmptyParts"
          :items="groupItemsEmptyParts"
          class="hidden-sm-and-up"
          item-title="text"
          item-value="value"
          solo
          text
          dense
          dark
          chips
          color="red-darken-2"
          background-color="grey-darken-4"
        />
      </v-col>
    </v-row>

    <!--Components inside div are rendered only when Empty Parts toolbar is expanded.-->
    <div v-if="showEmpty" class="pa-4 pt-8">
      <!--OPEN ALL QR CODES BUTTON-->
      <show-qr-codes class="ml-4 mb-1" @parts="openEmptyQrDialog" />
      <!--EMPTY PARTS COMPONENT-->
      <empty-parts
        ref="emptyParts"
        :groupby="groupEmptyParts"
        @qr="openEmptyQrDialog"
      />
      <!-- EMPTY PARTS QR DIALOG -->
      <empty-qr-dialog ref="emptyQrDialog" />
    </div>
    <!------------------------------------------SNACKBAR------------------------------------------->
    <v-snackbar v-model="snackbar.value" color="orange">
      {{ snackbar.text }}
      <v-btn text dark @click="snackbar.value = false"> Close </v-btn>
    </v-snackbar>
    <!--------------------------------------------------------------------------------------------->
    <car-dialog ref="carDialog" />
    <!--------------------------------------------------------------------------------------------->
    <v-dialog v-model="confirmDialog" width="auto">
      <v-card>
        <v-card-title class="headline">
          <v-alert class="mb-0" :value="true" type="error">
            {{ $t("delete_alert2", { count: approveItems.length }) }}
          </v-alert>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="grey" @click="cancelDelete">
            {{ $t("close") }}
          </v-btn>
          <v-btn
            color="red"
            text
            :loading="loadingDelete"
            @click="confirmDelete"
          >
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Toolbar from "@/components/InsertParts/toolbar.vue";
import Datatable from "@/components/InsertParts/datatable.vue";
import EditDialog from "@/components/InsertParts/editDialog.vue";
import QrScanner from "@/components/InsertParts/qrScanner.vue";
import ShipmentScanner from "@/components/InsertParts/shipmentScanner.vue";
import InsertEmptyDialog from "@/components/InsertParts/insertEmpty.vue";
import EmptyParts from "@/components/InsertParts/empty.vue";
import EmptyQrDialog from "@/components/InsertParts/qrCodesDialog.vue";
import ShowAllQrCodes from "@/components/InsertParts/showAllQrCodes.vue";
import ActionsDialog from "@/components/InsertParts/actionsDialog.vue";
import ClientsDialog from "@/components/Clients/editDialog.vue";
import ShipmentDialog from "@/components/Shipments/dialog.vue";
import MovementDialog from "@/components/movementDialog.vue";
import QrDialog from "@/components/qrDialog.vue";
import ImgCarousel from "@/components/imgCarousel.vue";
import ImgDialog from "@/components/imgDialog.vue";
import ImgEditor from "@/components/imgEditor.vue";
import NotesDialog from "@/components/notesDialog.vue";
import CarDialog from "@/components/carDialog.vue";
import putRequest from "../api/putRequest";
import deleteRequest from "../api/deleteRequest";
import ShareDialog from "@/components/InsertParts/shareDialog.vue";

export default {
  components: {
    toolbar: Toolbar,
    datatable: Datatable,
    "qr-dialog": QrDialog,
    "empty-qr-dialog": EmptyQrDialog,
    "img-carousel": ImgCarousel,
    "qr-scanner": QrScanner,
    "shipment-scanner": ShipmentScanner,
    "edit-dialog": EditDialog,
    "insert-empty-dialog": InsertEmptyDialog,
    "empty-parts": EmptyParts,
    "img-dialog": ImgDialog,
    "img-editor": ImgEditor,
    "notes-dialog": NotesDialog,
    "show-qr-codes": ShowAllQrCodes,
    "actions-dialog": ActionsDialog,
    "clients-dialog": ClientsDialog,
    "shipment-dialog": ShipmentDialog,
    "car-dialog": CarDialog,
    "movement-dialog": MovementDialog,
    "share-dialog": ShareDialog,
  },
  data: () => ({
    approveBtn: false,
    deleteBtn: false,
    approveItems: [],
    datatableItems: [],
    searchObject: {
      type: null,
      data: {},
    },
    hideEditDialog: false,
    showInserted: true,
    showEmpty: false,
    snackbar: {
      value: false,
      text: "",
    },
    insertItems: [
      { text: "Available", value: 1 },
      { text: "Waiting Approval", value: 2 },
      { text: "Review Needed", value: 3 },
      { text: "Refused", value: 4 },
      { text: "Approved", value: 5 },
    ],
    selectedInsertItems: [1, 3, 4, 5],
    groupItemsEmptyParts: [
      { text: "Group by Date", value: "date" },
      // { text: 'Group by Shipment', value: 'shipment' }, // disabled
    ],
    groupEmptyParts: { text: "Date", value: "date" },
    confirmDialog: false,
    loadingDelete: false,
  }),
  mounted() {
    this.getLocale();
    const { id } = this.$route.query;

    if (id) {
      const { toolbar } = this.$refs;
      toolbar.setId(id);
      toolbar.searchMethod();
    }
  },
  methods: {
    getLocale() {
      this.insertItems[0].text = this.$t("parts.status.available");
      this.insertItems[1].text = this.$t("parts.status.waiting_approval");
      this.insertItems[2].text = this.$t("parts.status.review_needed");
      this.insertItems[3].text = this.$t("parts.status.refused");
      this.insertItems[4].text = this.$t("parts.status.approved");
      this.groupItemsEmptyParts[0].text = this.$t(
        "parts.insert_parts.empty_parts.group_by_date",
      );
      // this.groupItemsEmptyParts[1].text = this.$i18n
      //     .t('parts.insert_parts.empty_parts.group_by_shipment');
    },
    toggleInsertStatus(value) {
      const selected = [...this.selectedInsertItems];
      const index = selected.indexOf(value);

      if (index === -1) {
        selected.push(value);
      } else {
        selected.splice(index, 1);
      }

      this.selectedInsertItems = selected;
    },
    getParts() {
      // Fired when saving or deleting part.
      // Get Inserted Parts.
      if (this.searchObject.type === null) {
        this.clearSearch();
      } else if (this.searchObject.type === "regularSearch") {
        this.search(this.searchObject.data);
      }
      // Get Empty Parts.
      if (this.showEmpty) {
        const { emptyParts } = this.$refs;
        emptyParts.getEmptyParts();
      }
    },
    /* SEARCH ------------------------------------------------------------------------------------*/
    search(data) {
      this.searchObject = {
        type: "regularSearch",
        data,
      };
    },
    clearSearch() {
      this.searchObject = {
        type: null,
        data: {},
      };
    },
    /* INSERT PARTS ------------------------------------------------------------------------------*/
    openInsertDialog() {
      const { insertEmptyDialog } = this.$refs;
      insertEmptyDialog.open();
    },
    /* APROVE PARTS ------------------------------------------------------------------------------*/
    approve(data) {
      this.approveBtn = data.value;
      this.approveItems = data.items;
      this.deleteBtn = data.value;
    },
    approveMethod() {
      const { toolbar } = this.$refs;
      toolbar.startLoading();
      const parts = [];
      this.approveItems.forEach((item) =>
        parts.push({ id: item._id, kit_id: item.kit_id }),
      );
      putRequest("/temporary-part/insert/approve", "", {
        parts,
        institution_id: this.$store.getters.institutionId,
        user_id: this.$store.getters.userId,
      })
        .then((response) => {
          if (response) {
            toolbar.stopLoading();
            this.approveBtn = false;
            this.approveItems = [];
            const { datatable } = this.$refs;
            datatable.uncheck();
            this.getParts();
          }
        })
        .catch(() => {
          toolbar.stopLoading();
        });
    },

    // deleteMethod() {
    //   const { toolbar } = this.$refs;
    //   const ids = this.approveItems.map(x => x._id);

    //   const counter = ids.length;
    //   // eslint-disable-next-line no-restricted-globals
    //   const c = confirm(this.$t('delete_alert2', { count: counter }));

    //   if (c === true) {
    //     toolbar.startLoadingDelete();
    //     deleteRequest('/temporary-part/insert', `ids=${ids}&user_id=${this.$store.getters.userId}&institution_id=${this.$store.getters.institutionId}`) // eslint-disable-next-line max-len
    //       .then((response) => {
    //         toolbar.stopLoadingDelete();
    //         this.getParts();
    //       })
    //       .catch((error) => {
    //         toolbar.stopLoadingDelete();
    //       });
    //   } else {
    //   }
    // },
    deleteMethod() {
      if (!this.approveItems.length) return;

      this.confirmDialog = true; // open confirmation dialog
    },
    confirmDelete() {
      const { toolbar } = this.$refs;
      const ids = this.approveItems.map((x) => x._id);

      this.loadingDelete = true;
      toolbar.startLoadingDelete();

      deleteRequest(
        "/temporary-part/insert",
        `ids=${ids}&user_id=${this.$store.getters.userId}&institution_id=${this.$store.getters.institutionId}`,
      )
        .then(() => {
          toolbar.stopLoadingDelete();
          this.getParts();
          this.confirmDialog = false;
          this.loadingDelete = false;
        })
        .catch(() => {
          toolbar.stopLoadingDelete();
          this.loadingDelete = false;
          this.confirmDialog = false;
        });
    },
    cancelDelete() {
      this.confirmDialog = false;
    },

    /* DATATABLE ITEMS ---------------------------------------------------------------------------*/
    getDatatableItems(data) {
      this.datatableItems = data;
    },
    /* QR DIALOG ---------------------------------------------------------------------------------*/
    openQrDialog(data) {
      const { qrDialog } = this.$refs;
      qrDialog.open(data);
    },
    openEmptyQrDialog(data) {
      const { emptyQrDialog } = this.$refs;
      emptyQrDialog.open(data);
    },
    /* IMAGES CAROUSEL ---------------------------------------------------------------------------*/
    openImagesCarousel(data) {
      const { imgCarousel } = this.$refs;
      imgCarousel.open(data);
    },
    /* EDIT DIALOG -------------------------------------------------------------------------------*/
    openEditDialog(data, kit) {
      const { editDialog } = this.$refs;
      editDialog.open(true, kit, data);
    },
    /* Actions Dialog --------------------------------------------------------------------------- */
    openActionsDialog(data) {
      const { actionsDialog } = this.$refs;
      actionsDialog.open(data);
    },
    /* Clients Dialog --------------------------------------------------------------------------- */
    openClientsDialog() {
      const { clientsDialog } = this.$refs;
      const { actionsDialog } = this.$refs;
      clientsDialog.open(false, null, this.$store.getters.institutionId);
      actionsDialog.hide();
    },
    getClients() {
      const { actionsDialog } = this.$refs;
      actionsDialog.getClients();
    },
    showActionsDialog() {
      const { actionsDialog } = this.$refs;
      actionsDialog.show();
    },
    /* KIT ---------------------------------------------------------------------------------------*/
    scanMainPart() {
      const { qrScanner } = this.$refs;
      // this.hideEditDialog = true;
      qrScanner.open({ mode: "kit" });
    },
    selectMainPart(part) {
      const { editDialog } = this.$refs;
      editDialog.setMainPart(part);
      this.hideEditDialog = false;
    },
    closeQrScanner() {
      this.hideEditDialog = false;
    },
    /* IMAGE DIALOG ----------------------------------------------------------------------------- */
    openImgDialog(image) {
      const { imgDialog } = this.$refs;
      imgDialog.open(image);
      this.hideEditDialog = true;
    },
    openImgEditor(image) {
      const { imgEditor } = this.$refs;
      imgEditor.open(image);
      this.hideEditDialog = true;
    },
    editImage(image) {
      const { editDialog } = this.$refs;
      editDialog.setEditedImage(image);
      this.hideEditDialog = false;
    },
    closeImgDialog() {
      this.hideEditDialog = false;
    },
    /* SHIPMENT DIALOG -------------------------------------------------------------------------- */
    openShipmentDialog() {
      const { shipmentDialog } = this.$refs;
      const { editDialog } = this.$refs;
      shipmentDialog.open(false, null);
      // editDialog.hideEditDialog(true);
    },
    closeShipmentDialog() {
      const { editDialog } = this.$refs;
      // editDialog.hideEditDialog(false);
    },
    saveShipment() {
      const { editDialog } = this.$refs;
      editDialog.getShipments();
    },
    /* SHIPMENT SCANNER ------------------------------------------------------------------------- */
    openShipmentScanner() {
      const { shipmentScanner } = this.$refs;
      // const { editDialog } = this.$refs;
      shipmentScanner.open();
      // editDialog.hideEditDialog(true);
    },
    closeShipmentScanner() {
      const { editDialog } = this.$refs;
      // editDialog.hideEditDialog(false);
    },
    selectShipment(shipmentId) {
      const { editDialog } = this.$refs;
      editDialog.setShipment(shipmentId);
    },
    /* QR SCANNER ------------------------------------------------------------------------------- */
    openQrScanner() {
      const { qrScanner } = this.$refs;
      qrScanner.open({ mode: "single" });
    },
    /* CAR DIALOG ------------------------------------------------------------------------------- */
    openCarDialog(data) {
      const { carDialog } = this.$refs;
      carDialog.open(data);
    },
    /* Notes dialog ----------------------------------------------------------------------------- */
    openNotesDialog(notes) {
      const { notesDialog } = this.$refs;
      notesDialog.open(notes);
    },
    /* SNACKBAR --------------------------------------------------------------------------------- */
    activateSnackbar(data) {
      this.snackbar = {
        value: true,
        text: data,
      };
    },
    /* Movement Dialog -------------------------------------------------------------------------- */
    openMovementDialog(data) {
      const { movementDialog } = this.$refs;
      movementDialog.open(data);
    },
    /* Share Dialog ----------------------------------------------------------------------------- */
    openShareDialog(data) {
      const { shareDialog } = this.$refs;
      shareDialog.open(this.selected ? this.selectedItems : [data]);
    },
  },
};
</script>

<style scoped>
.inserted-parts-mobile-status {
  background: #212121;
  padding: 10px 12px 12px;
}

.inserted-parts-mobile-status__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.inserted-parts-mobile-status__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #ef5350;
}

.inserted-parts-mobile-status__count {
  flex-shrink: 0;
  font-size: 0.7rem;
  color: #9e9e9e;
}

.inserted-parts-mobile-status__scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 2px;
}

.inserted-parts-mobile-status__scroll::-webkit-scrollbar {
  display: none;
}

.inserted-parts-mobile-status__chip {
  flex-shrink: 0;
  border: 1px solid #616161;
  border-radius: 999px;
  padding: 7px 12px;
  background: transparent;
  color: #eeeeee;
  font-size: 0.75rem;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
}

.inserted-parts-mobile-status__chip--active {
  background: #c62828;
  border-color: #c62828;
  color: #fff;
  font-weight: 600;
}
</style>
